import { splitList, type CsvField } from '~/components/csv-import/model/schema';

/** Beyond this many, a list cell names how many more it holds rather than showing them. */
const MAX_PILLS = 2;

/** Whether a column's values read better as separate pills than as the cell's text. */
export const showsAsPills = (field: CsvField) => field.multiple || field.type === 'enum';

export type PillTone = 'plain' | 'invalid' | 'duplicate' | 'removed' | 'added';

const PILL_TONES: Record<PillTone, string> = {
  plain: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200',
  invalid: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300',
  duplicate: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300',
  removed: 'bg-red-50 text-red-600 line-through dark:bg-red-500/10 dark:text-red-400',
  added: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
};

/** Held to one line; what does not fit is counted, and the cell's title lists it all. */
export const Pills = ({ raw, tone }: { raw: string; tone: PillTone }) => {
  const values = splitList(raw);
  if (values.length === 0) {
    return <span className="text-neutral-300 dark:text-neutral-600">—</span>;
  }
  const hidden = values.length - MAX_PILLS;
  return (
    <div className="flex min-w-0 flex-nowrap items-center gap-1 overflow-hidden">
      {values.slice(0, MAX_PILLS).map((value, index) => (
        <span
          key={index}
          // Values shrink before the count: a clipped "+2 more" would hide that more exist.
          className={`min-w-0 max-w-[8rem] truncate rounded-full px-1.5 py-px text-[11px] leading-4 ${PILL_TONES[tone]}`}
        >
          {value}
        </span>
      ))}
      {hidden > 0 && (
        <span className="shrink-0 rounded-full px-1.5 py-px text-[11px] leading-4 text-neutral-500 ring-1 ring-inset ring-neutral-200 dark:text-neutral-400 dark:ring-neutral-600">
          +{hidden} more
        </span>
      )}
    </div>
  );
};
